import React from 'react';
import { MotionMode, SongAdScene, TelopPosition, TelopSize } from '../../services/api/songAdService';
import { SongAdSceneDraft } from './types';
import { TaskyButton, TaskyInput, TaskySelect, TaskyEmptyState, TaskyAlert } from '../tasky';
import {
  Video,
  RefreshCw,
  Loader2,
  AlertCircle,
  Lock,
  Sparkles,
  Image as ImageIcon,
  EyeOff,
  Wand2,
  ArrowRight,
} from 'lucide-react';

/**
 * ステップ④: モーション。
 *
 * シーンごとに「動画化する（clip）/ 静止画で使う（still）/ 使わない（skip）」の3値を選び、
 * 動画化するシーンだけ絵コンテを開始フレームとしてクリップを生成する（spec §11）。
 * v2の boolean `animate` を `motion_mode` に置き換え、一括操作＋AIにおまかせ（motion/auto）を追加。
 *
 * mockup.html は「動画を組み立てる」の単発アクションでクリップ生成込みの一括処理を模擬しているが、
 * 実装は既存のSeedance非同期ジョブ基盤（シーン単位 POST .../clip）をそのまま使うため、
 * モーション設定（motion_mode/motion_prompt）とクリップ生成トリガーは本コンポーネント内で両方担う
 * （意図的な差分。GOAL.mdのB4/B5にクリップ生成トリガーの再設計は含まれないため）。
 */

interface MotionStepProps {
  scenes: SongAdScene[];
  sceneDrafts: SongAdSceneDraft[];
  aspectRatio: string;
  generatingSceneIds: Set<string>;
  isSavingDrafts: boolean;
  isMotionAutoRequesting?: boolean;
  onSetMotionMode: (sceneId: string, mode: MotionMode) => void;
  onSetMotionModeAll: (mode: MotionMode) => void;
  onMotionPromptChange: (sceneId: string, prompt: string) => void;
  onTelopPositionChange: (sceneId: string, position: TelopPosition) => void;
  onTelopSizeChange: (sceneId: string, size: TelopSize) => void;
  /** v8: 「保存して次へ」。保存成功後に⑥プレビューへ自動遷移する（呼び出し元が遷移を担当） */
  onSaveAndProceed: () => Promise<void>;
  onMotionAuto: () => Promise<void>;
  onGenerate: (sceneId: string) => void;
  onGenerateAll: () => void;
}

const aspectClass = (ratio: string): string => {
  switch (ratio) {
    case '9:16':
      return 'aspect-[9/16]';
    case '1:1':
      return 'aspect-square';
    default:
      return 'aspect-video';
  }
};

const PROGRESS_STEP_LABELS: Record<string, string> = {
  initializing: '初期化中',
  optimizing_prompt: '準備中',
  generating: '動画を生成中',
  downloading: 'ダウンロード中',
  uploading: 'アップロード中',
  saving: '保存中',
};

const MOTION_MODE_OPTIONS: { key: MotionMode; label: string }[] = [
  { key: 'clip', label: '動画化' },
  { key: 'still', label: '静止画' },
  { key: 'skip', label: '使わない' },
];

const MotionStep: React.FC<MotionStepProps> = ({
  scenes,
  sceneDrafts,
  aspectRatio,
  generatingSceneIds,
  isSavingDrafts,
  isMotionAutoRequesting = false,
  onSetMotionMode,
  onSetMotionModeAll,
  onMotionPromptChange,
  onTelopPositionChange,
  onTelopSizeChange,
  onSaveAndProceed,
  onMotionAuto,
  onGenerate,
  onGenerateAll,
}) => {
  if (scenes.length === 0) {
    return (
      <TaskyEmptyState
        icon={<Video />}
        title="先にストーリーボードを作成してください"
        description="「③ ストーリーボード」でシーンを作成するとモーションを設定できます"
      />
    );
  }

  const draftFor = (sceneId: string): SongAdSceneDraft | undefined =>
    sceneDrafts.find((d) => d.id === sceneId);
  const modeFor = (scene: SongAdScene): MotionMode => draftFor(scene.id)?.motionMode ?? scene.motion_mode;

  const readyScenes = scenes.filter(
    (s) =>
      modeFor(s) === 'clip' &&
      s.storyboard.image &&
      (s.clip.status === 'none' || s.clip.status === 'failed')
  );
  const activeCount = scenes.filter(
    (s) => s.clip.status === 'pending' || s.clip.status === 'processing'
  ).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-gray-600">
          「動画化」にしたシーンだけクリップを生成します。「静止画」はそのまま、「使わない」は合成から除外されます。
        </p>
        {/* v8: 「未生成を一括生成」と「保存して次へ」の位置を入れ替え、①情報登録・②歌詞の
            「保存して次へ」（primaryボタンを右端に置く並び）に揃えた。保存成功で⑥プレビューへ遷移する */}
        <div className="flex items-center gap-2">
          <TaskyButton variant="outline" size="sm" onClick={onGenerateAll} disabled={readyScenes.length === 0}>
            <Sparkles className="w-3.5 h-3.5" />
            未生成を一括生成（{readyScenes.length}件）
          </TaskyButton>
          <TaskyButton size="sm" onClick={() => onSaveAndProceed()} isLoading={isSavingDrafts}>
            <ArrowRight className="w-3.5 h-3.5" />
            保存して次へ
          </TaskyButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => onSetMotionModeAll('clip')}
          className="text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
        >
          すべて動画化
        </button>
        <button
          type="button"
          onClick={() => onSetMotionModeAll('still')}
          className="text-xs text-gray-600 bg-warm-surface hover:bg-gray-100 rounded-lg px-3 py-2 transition-colors"
        >
          すべて静止画
        </button>
        <button
          type="button"
          onClick={() => void onMotionAuto()}
          disabled={isMotionAutoRequesting}
          className="text-xs text-white bg-dark hover:opacity-90 rounded-lg px-3 py-2 transition-colors flex items-center gap-1 disabled:opacity-50"
        >
          {isMotionAutoRequesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
          <span>AIにおまかせ</span>
        </button>
      </div>

      {activeCount > 0 && (
        <TaskyAlert tone="info" icon={<Loader2 className="w-5 h-5 text-primary animate-spin" />} className="mb-4">
          {activeCount}件のクリップを生成中です（1本あたり数分）。完了すると自動で表示されます。
        </TaskyAlert>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {scenes.map((scene, index) => {
          const status = scene.clip.status;
          const isRequesting = generatingSceneIds.has(scene.id);
          const isActive = status === 'pending' || status === 'processing' || isRequesting;
          const isLocked = !scene.storyboard.image;
          const draft = draftFor(scene.id);
          const mode = modeFor(scene);
          const skipped = mode === 'skip';

          return (
            <div
              key={scene.id}
              className={`border border-warm-border rounded-xl overflow-hidden bg-white ${skipped ? 'opacity-60' : ''}`}
            >
              <div className={`${aspectClass(aspectRatio)} bg-gray-100 relative flex items-center justify-center`}>
                {isLocked ? (
                  <div className="flex flex-col items-center text-gray-400 px-4 text-center">
                    <Lock className="w-7 h-7 mb-2" />
                    <span className="text-xs">先に「③ ストーリーボード」を生成してください</span>
                  </div>
                ) : mode === 'clip' && scene.clip.video ? (
                  <video src={scene.clip.video.url} controls preload="metadata" className="w-full h-full object-cover" />
                ) : mode === 'clip' && isActive ? (
                  <div className="flex flex-col items-center text-gray-400 w-full px-6">
                    <Loader2 className="w-7 h-7 animate-spin mb-2" />
                    <span className="text-xs mb-2">
                      {PROGRESS_STEP_LABELS[scene.clip.progress_step ?? ''] ?? '生成中'}...
                    </span>
                    {scene.clip.progress_percentage != null && (
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-primary h-1.5 rounded-full transition-all"
                          style={{ width: `${scene.clip.progress_percentage}%` }}
                        />
                      </div>
                    )}
                  </div>
                ) : mode === 'clip' && status === 'failed' ? (
                  <div className="flex flex-col items-center text-red-400 px-4 text-center">
                    <AlertCircle className="w-7 h-7 mb-2" />
                    <span className="text-xs line-clamp-3">{scene.clip.error_message ?? '生成に失敗しました'}</span>
                  </div>
                ) : (
                  <img src={scene.storyboard.image!.url} alt="" className="w-full h-full object-cover" />
                )}
                {skipped && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <span className="text-xs text-gray-600 bg-white rounded-full px-3 py-1 shadow flex items-center gap-1">
                      <EyeOff className="w-3 h-3" />
                      使用しない
                    </span>
                  </div>
                )}
                {mode === 'still' && !skipped && (
                  <span className="absolute top-2 right-2 bg-white/85 text-gray-600 text-[10px] rounded-full px-2 py-0.5 flex items-center gap-0.5">
                    <ImageIcon className="w-3 h-3" />
                    静止画
                  </span>
                )}
              </div>

              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">シーン {index + 1}</span>
                  {mode === 'clip' && (
                    <button
                      onClick={() => onGenerate(scene.id)}
                      disabled={isLocked || isActive || !scene.clip_prompt}
                      className="flex items-center gap-1 px-2 py-1 text-xs border border-warm-border rounded-lg hover:bg-gray-100 disabled:opacity-50"
                    >
                      <RefreshCw className={`w-3 h-3 ${isRequesting ? 'animate-spin' : ''}`} />
                      {scene.clip.video || status === 'failed' ? '再生成' : '生成'}
                    </button>
                  )}
                </div>

                <div className="inline-flex w-full rounded-lg overflow-hidden border border-warm-border text-xs">
                  {MOTION_MODE_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => onSetMotionMode(scene.id, opt.key)}
                      className={`flex-1 py-1.5 transition-colors ${
                        mode === opt.key ? 'bg-primary text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {mode === 'clip' && (
                  <TaskyInput
                    value={draft?.motionPrompt ?? scene.motion_prompt ?? ''}
                    onChange={(e) => onMotionPromptChange(scene.id, e.target.value)}
                    placeholder="モーションの指示（例：商品にゆっくりズームする）"
                    className="text-xs"
                  />
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">テロップ位置</label>
                    <TaskySelect
                      value={draft?.telopPosition ?? scene.telop_position}
                      onChange={(e) => onTelopPositionChange(scene.id, e.target.value as TelopPosition)}
                      className="text-xs"
                    >
                      <option value="top">上</option>
                      <option value="middle">中</option>
                      <option value="bottom">下</option>
                    </TaskySelect>
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">テロップサイズ</label>
                    <TaskySelect
                      value={draft?.telopSize ?? scene.telop_size}
                      onChange={(e) => onTelopSizeChange(scene.id, e.target.value as TelopSize)}
                      className="text-xs"
                    >
                      <option value="sm">小</option>
                      <option value="md">中</option>
                      <option value="lg">大</option>
                    </TaskySelect>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MotionStep;
